import { FieldWithInlineEdit, objectHelper } from '@tap/mui-ui';
import { GridSize } from '@mui/material';
import { SelectViewField, SelectEditField } from './dynamic-form//fields/SelectField';
import { StatusViewField } from './dynamic-form/fields/StatusField';
import { IApiDocumentSchema, DocumentEntityType, DocumentFieldType, IDocumentEntityFieldProps, IDocumentFieldProps } from '@tap/data-providers';
import { TapPriorityList, UserField } from '@tap/tap-ui';
import { dateTimeHelper } from '@tap/utils';


export type DocumentPropertyListProps = {
    fieldListKey: string,
    document: any,
    documentSchema: IApiDocumentSchema,
    md?: GridSize,
    setDocument: React.Dispatch<React.SetStateAction<any>>,
};

export enum FieldMode {
    View,
    Edit,
}

export type FieldComponentProps = {
    fieldMode: FieldMode,
    documentSchema: IApiDocumentSchema,
    fieldProps: IDocumentFieldProps,
    fieldValue: any,
    onChange?: any,
}

export const ViewFieldComponent: React.FunctionComponent<Omit<FieldComponentProps, 'fieldMode'>> = (props) => {
    return (<FieldComponent fieldMode={FieldMode.View} {...props} />);
}

export const FieldComponent = ({
    fieldMode,
    documentSchema,
    fieldProps,
    fieldValue,
    onChange,
}: FieldComponentProps) => {

    if (!fieldValue) {
        return (<>{'-'}</>);
    }

    const documentSettings = documentSchema.settings;

    if (fieldProps.type === DocumentFieldType.Identifier) {
        const identifierProps = fieldProps.typeProps;

        const sourceList = documentSettings[identifierProps.sourceListPropertyKey];
        const selectedValue = sourceList.find(p => p.code === fieldValue);

        const selectSx = {
            height: '25px',
            '& div.MuiSelect-select': {
                height: '25px',
                paddingLeft: '7px !important',
            },
        }

        return fieldMode === FieldMode.View
            ? (<SelectViewField sourceList={sourceList} value={fieldValue} />)
            : (<SelectEditField
                sourceList={sourceList}
                value={fieldValue}
                onChange={onChange}
                selectSx={selectSx} />);
    }

    if (fieldProps.type === DocumentFieldType.Priority) {
        const sourceList = TapPriorityList;
        const iconSx = {
            borderRadius: '4px',
            color: '#ffffff',
            fontSize: '16px',
            marginBottom: '-3px',
            marginLeft: '1px',
            marginRight: '8px',
        };

        const selectSx = {
            height: '25px',
            '& div.MuiSelect-select': {
                height: '25px',
                paddingLeft: '7px !important',
            },
        }


        return fieldMode === FieldMode.View
            ? (<SelectViewField sourceList={sourceList}
                value={fieldValue}
                iconSx={iconSx} />)
            : (<SelectEditField
                iconSx={{
                    ...iconSx,
                    marginTop: '4px',
                }}
                selectSx={selectSx}
                sourceList={sourceList}
                value={fieldValue}
                onChange={onChange} />);
    }

    if (fieldProps.type === DocumentFieldType.Status) {
        const sourceList = documentSettings.statusList;
        const selectedValue = sourceList.find(p => p.code === fieldValue);

        return fieldMode === FieldMode.View
            ? (<StatusViewField value={selectedValue} />)
            : <></>;
    }

    if (fieldProps.type === DocumentFieldType.String || fieldProps.type === DocumentFieldType.Number) {
        const stringProps = fieldProps.typeProps;

        return fieldMode === FieldMode.View
            ? (<>{fieldValue || stringProps?.defaultValue}</>)
            : <></>;
    }


    if (fieldProps.type === DocumentFieldType.User) {
        return fieldMode === FieldMode.View
            ? (<>{<UserField user={fieldValue} />}</>)
            : <></>;
    }


    if (fieldProps.type === DocumentFieldType.UtcDate) {
        const defaultDateFormat = 'dd.MM.yyyy HH:mm:ss';

        const utcDateProps = fieldProps.typeProps;

        return fieldMode === FieldMode.View
            ? (<>{dateTimeHelper.format(fieldValue, utcDateProps.displayFormat || defaultDateFormat)}</>)
            : <></>;
    }


    if (fieldProps.type === DocumentFieldType.Entity) {

        const entityProps = fieldProps.typeProps as IDocumentEntityFieldProps;

        if (entityProps.entityType === DocumentEntityType.Department) {

            const value = fieldProps.fieldDesc?.propertyName
                ? fieldValue[fieldProps.fieldDesc?.propertyName]
                : `${fieldValue.manufacture}.${fieldValue.sector}`;


            return fieldMode === FieldMode.View
                ? (<>{value}</>)
                : <></>;

        }
    }


    return <>{'-'}</>
}

const getFormatValue = (format: string, document: any) => {
    const result = format.replace(/{([a-zA-Z0-9]+)}/g, (match, fieldKey) => {
        return typeof document.data[fieldKey] != 'undefined'
            ? document.data[fieldKey]
            : '';
    });

    return (result || '').trim();
};


export const DocumentPropertyList = ({
    fieldListKey,
    document,
    documentSchema,
    setDocument,
    md = 12,
}: DocumentPropertyListProps) => {


    const onChange = (propertyKey: string, newValue: any) => {
        setDocument(objectHelper.setDeepProperty(document, propertyKey, newValue));
    }

    const fieldList = (documentSchema.settings[fieldListKey] || [])
        .map((fieldDesc) => {

            if (typeof fieldDesc === 'string' || fieldDesc instanceof String) {
                return { propertyKey: fieldDesc, res: documentSchema.settings.dataProperties[fieldDesc] };
            } else if (fieldDesc.viewFormat) {
                const viewValue = getFormatValue(fieldDesc.viewFormat, document);
                return { propertyKey: fieldDesc.fieldName, res: documentSchema.settings.dataProperties[fieldDesc.fieldName], fieldDesc, viewValue };
            } else {
                return { propertyKey: fieldDesc.fieldName, res: documentSchema.settings.dataProperties[fieldDesc.fieldName], fieldDesc };
            }
        })
        .filter(({ res }) => res)
        .map(({ propertyKey, res, fieldDesc, viewValue }) => ({ ...res, propertyKey, fieldDesc, viewValue }));

    return (<>

        {fieldList.map((field, index) => {
            const fieldValue = field.sourceObject === 'data'
                ? document.data[field.propertyKey]
                : document[field.propertyKey];

            return (<FieldWithInlineEdit
                key={field.propertyKey + '_' + index}
                md={md}
                title={field.fieldDesc?.title || field.name}
                view={<FieldComponent
                    fieldMode={FieldMode.View}
                    documentSchema={documentSchema}
                    fieldProps={field}
                    fieldValue={field.viewValue || fieldValue} />}
                edit={field.editProps?.allowEditOnDetailsPage ? (<FieldComponent
                    fieldMode={FieldMode.Edit}
                    documentSchema={documentSchema}
                    fieldProps={field}
                    onChange={(newValue: any) => { onChange(field.sourceObject === 'data' ? `data.${field.propertyKey}` : field.propertyKey, newValue) }}
                    fieldValue={fieldValue} />) : undefined}
            />)
        },
        )}

    </>);
};